import _debounce from 'lodash/debounce';
import { useCallback, useRef, useState, RefObject, useEffect } from 'react';
import { Box, IconButton, Stack, Typography, Alert, useTheme } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useLocation } from 'react-router-dom';
import { useHeaderContext } from '../contexts/HeaderContext';
import { ValidationType } from '../utils/ValidationType';

export const BottomDrawer = () => {
  const { pathname } = useLocation();

  if (pathname !== '/course-reservation') return <></>;

  return <CourseReservationDrawer />;
};

const CourseReservationDrawer = () => {
  const theme = useTheme();
  const { formProgress } = useHeaderContext();
  const contentRef = useRef<HTMLDivElement>(null);

  const [initialRenderDone, setInitialRenderDone] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [translateSize, setTranslateSize] = useState(0);

  const toggleContent = useCallback(() => {
    setShowContent((prevOpen) => !prevOpen);
  }, []);

  const toggleTranslateSize = useCallback(() => {
    if (!contentRef.current) return;

    setTranslateSize(showContent ? 0 : contentRef.current.offsetHeight + 1);
  }, [showContent]);

  useEffect(() => {
    const handleResize = _debounce(() => {
      toggleTranslateSize();
    }, 150);

    const observer = new ResizeObserver(() => {
      handleResize();
    });

    if (contentRef.current) observer.observe(contentRef.current);

    return () => {
      observer.disconnect();
      handleResize.cancel();
    };
  }, [toggleTranslateSize, showContent]);

  useEffect(() => {
    if (!initialRenderDone) {
      setInitialRenderDone(true);
    }
  }, [initialRenderDone]);

  useEffect(() => {
    if (formProgress === 100 && !showContent) {
      setShowContent(true);
      setTranslateSize(0);
    } else {
      toggleTranslateSize();
    }
  }, [toggleTranslateSize, formProgress, showContent]);

  // transition timing
  const transformDuration = theme.transitions.duration.leavingScreen;
  const opacityDuration = 400;
  const opacityDelay = 100;

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        opacity: initialRenderDone ? 1 : 0,
        bgcolor: 'white',
        zIndex: theme.zIndex.drawer - 10,
        boxShadow: `0px -12px 16px -4px ${theme.palette.shadows.dark}`,
        transform: `translateY(${translateSize}px)`,
        transition: `
          transform ${transformDuration}ms ${theme.transitions.easing.sharp},
          opacity ${opacityDuration}ms ${theme.transitions.easing.easeIn} ${opacityDelay}ms
        `,
      }}
    >
      <AlwaysVisibleContent open={showContent} toggleContent={toggleContent} />
      <ShowUpContent contentRef={contentRef} />
    </Box>
  );
};

type AlwaysVisibleContentProps = {
  open: boolean;
  toggleContent: () => void;
};

const AlwaysVisibleContent = ({ open, toggleContent }: AlwaysVisibleContentProps) => {
  const theme = useTheme();
  const { triggerSubmit, isFormLoading } = useHeaderContext();
  const { formProgress } = useHeaderContext();

  return (
    <Box display="flex" justifyContent="center" alignItems="center" width="100%" p={2} gap={1}>
      <LoadingButton variant="outlined" color="secondary" fullWidth loading={isFormLoading} onClick={() => triggerSubmit(ValidationType.Draft)}>
        Save Draft
      </LoadingButton>

      {formProgress < 100 && (
        <IconButton onClick={toggleContent} color="primary" sx={{ ...theme.mixins.roundedIconButton }}>
          {open ? <ExpandMoreIcon /> : <ExpandLessIcon />}
        </IconButton>
      )}
    </Box>
  );
};

type ShowUpContentProps = {
  contentRef: RefObject<HTMLDivElement>;
};

const ShowUpContent = ({ contentRef }: ShowUpContentProps) => {
  const { triggerSubmit, isFormLoading } = useHeaderContext();
  const { formProgress } = useHeaderContext();

  const submitHandler = () => {
    triggerSubmit(ValidationType.Full);
  };

  return (
    <Stack ref={contentRef} px={2} pb={2} spacing={2}>
      <SubmitApplicationButton isProgressComplete={formProgress === 100} isFormLoading={isFormLoading} handleSubmit={submitHandler} />
      {formProgress < 100 && <IncompleteApplicationAlert triggerValidation={submitHandler} isFormLoading={isFormLoading} />}
    </Stack>
  );
};

const SubmitApplicationButton = ({
  isProgressComplete,
  isFormLoading,
  handleSubmit,
}: {
  isProgressComplete: boolean;
  isFormLoading: boolean;
  handleSubmit: () => void;
}) => {
  return (
    <LoadingButton
      fullWidth
      loading={isFormLoading}
      onClick={handleSubmit}
      variant={isProgressComplete ? 'contained' : 'outlined'}
      color={isProgressComplete ? 'primary' : 'secondary'}
    >
      Submit Application
    </LoadingButton>
  );
};

const IncompleteApplicationAlert = ({ triggerValidation, isFormLoading }: { triggerValidation: () => void; isFormLoading: boolean }) => (
  <Alert variant="outlined" severity="warning" color="info" sx={{ flexDirection: 'column' }}>
    <Stack gap={2}>
      <Typography variant="body1">Incomplete Application</Typography>
      <Typography variant="body2">
        Please complete all required fields to be able to submit the application. Click the button below to highlight all incomplete fields.
      </Typography>

      <LoadingButton variant="outlined" color="warning" fullWidth onClick={triggerValidation} loading={isFormLoading}>
        Highlight Incomplete Fields
      </LoadingButton>
    </Stack>
  </Alert>
);
